/**
 * BackgroundStreamService - Manages background WebSocket streaming via Web Worker
 *
 * When users navigate away from ProjectChatScreen during active generation,
 * this service keeps the WebSocket connection alive, buffers messages via a
 * Web Worker, and auto-progresses through the workflow:
 *
 *   mapping → [actionNeeded: select endpoints] → testconfig → permutation → [actionNeeded: return]
 *
 * Only endpoint selection requires user action. All other steps auto-trigger.
 *
 * @module services/BackgroundStreamService
 */

import { chatStreamService } from '../../../shared/workers/ChatStreamService';
import { useGenerationStore } from '../../../store/generation/generationStore';
import { useProjectsStore } from '../../../store/projects/projectsStore';
import EndpointParser from '../../../shared/middleware/utils/EndpointParser';
import { addLinkingColumnsToRequestCSV } from '../../../shared/utils/dataSourceLinkingUtils';
import { ProjectService } from '../../../shared/middleware/services/ProjectService';
import { FileService } from '../../../shared/middleware/services/FileService';
import JsonConfigPopulator from '../../../shared/middleware/utils/JsonConfigPopulator';
import { resolveScriptFolderNames, resolveScriptFolderName } from '../../../shared/middleware/utils/scriptFolderName';
import { determineMimeType } from '../utils/fileTypeUtils';
import logger from '../../../shared/services/logging/logger';

class BackgroundStreamService {
  constructor() {
    this.worker = null;
    this.unregisterHandler = null;
    this.active = false;
    this.conversationId = null;
    this.connectionHeld = false;

    // Workflow context for auto-progression
    this.userData = null;
    this.selectedEndpoints = [];
    this.generatedTestConfigs = [];
    this.currentEndpointIndex = 0;
    this.currentPhase = null; // 'mapping' | 'testconfig' | 'permutation'
    this.buffer = '';
    this.completedPermutations = [];

    // Project creation context
    this.projectInfo = null;
    this.isTestSuiteMode = false;
    this.existingProjectId = null;
    this.existingProjectName = null;
    this.customFolderNames = {};
  }

  /**
   * Start background streaming with full workflow context.
   *
   * @param {Object} options
   * @param {string} options.conversationId - Active conversation ID
   * @param {string|null} options.activeGenerationType - Current generation type
   * @param {string} options.existingBuffer - Any existing buffer content
   * @param {Object} options.userData - User auth data (userId, tenantId, buckets)
   * @param {Array} options.selectedEndpoints - Selected endpoints for auto-progression
   * @param {Array} options.generatedTestConfigs - Already completed test configs
   * @param {number} options.currentEndpointIndex - Current endpoint being processed
   * @param {Object} options.projectInfo - Project metadata for auto-creation
   * @param {boolean} options.isTestSuiteMode - Whether in test suite mode
   * @param {string} options.existingProjectId - Existing project ID (test suite mode)
   * @param {string} options.existingProjectName - Existing project name (test suite mode)
   * @param {Object} options.customFolderNames - User-defined folder name overrides
   */
  start({ conversationId, activeGenerationType, existingBuffer, userData, selectedEndpoints, generatedTestConfigs, currentEndpointIndex, projectInfo, isTestSuiteMode, existingProjectId, existingProjectName, customFolderNames, mappingS3Files }) {
    if (this.active) {
      logger.warn('BackgroundStreamService: Already active, stopping previous');
      this.stop();
    }

    logger.info('BackgroundStreamService: Starting', {
      conversationId,
      activeGenerationType,
      selectedEndpoints: selectedEndpoints?.length,
      currentEndpointIndex
    });

    this.active = true;
    this.conversationId = conversationId;
    this.currentPhase = activeGenerationType;
    this.buffer = existingBuffer || '';

    // Workflow context
    this.userData = userData;
    this.selectedEndpoints = selectedEndpoints || [];
    this.generatedTestConfigs = generatedTestConfigs ? [...generatedTestConfigs] : [];
    this.completedPermutations = [];
    this.currentEndpointIndex = currentEndpointIndex || 0;
    this.projectInfo = projectInfo || null;
    this.isTestSuiteMode = isTestSuiteMode || false;
    this.existingProjectId = existingProjectId || null;
    this.existingProjectName = existingProjectName || null;
    this.customFolderNames = customFolderNames || {};
    this._initialFiles = mappingS3Files || []; // S3 file refs for batch mapping continuation
    this._accumulatedEndpoints = null; // Accumulated endpoints across mapping batches

    // Keep the worker-based connection alive during background operation
    chatStreamService.connect();
    chatStreamService.setBackgroundHold(true); // no-op; worker survives navigation naturally
    this.connectionHeld = true;

    // Spawn Web Worker for message processing
    this.worker = new Worker(
      new URL('../../../shared/workers/backgroundStream.worker.js', import.meta.url)
    );

    // Initialize worker
    this.worker.postMessage({
      action: 'init',
      data: {
        activeGenerationType: activeGenerationType || null,
        existingBuffer: existingBuffer || ''
      }
    });

    // Handle worker events
    this.worker.onmessage = (event) => {
      const { type, data } = event.data;

      switch (type) {
        case 'streamComplete':
          logger.info('BackgroundStreamService: Stream completed', {
            generationType: data.generationType,
            messageCount: data.messageCount
          });
          // Use main thread buffer if worker result doesn't have raw content
          if (!data.result?.raw && this.buffer) {
            data.result = { ...data.result, raw: this.buffer };
          }
          this._handleStreamComplete(data.generationType, data.result);
          this.buffer = ''; // Clear buffer after processing
          break;

        case 'streamError':
          logger.error('BackgroundStreamService: Stream error', { message: data.message });
          this._setActionNeeded('Generation encountered an error. Return to retry.');
          break;

        case 'progress':
          logger.debug('BackgroundStreamService: Progress', data);
          this._updateProgress();
          break;

        default:
          break;
      }
    };

    this.worker.onerror = (err) => {
      logger.error('BackgroundStreamService: Worker crashed', { error: err.message });
    };

    this.unregisterHandler = chatStreamService.registerHandler((message) => {
      if (!this.worker || !this.active) return;

      this.worker.postMessage({ action: 'message', data: message });

      // Track buffer in main thread for EndpointParser access on completion
      if (message.type === 'stream' && message.content) {
        this.buffer += message.content;
      } else if (message.type === 'complete') {
        // Just mark buffer ready — worker's streamComplete event drives auto-progression
        // (Handling completion here too caused duplicate test config entries)
        this.buffer = this.buffer; // keep buffer for worker's streamComplete handler
      }
    });
  }

  /**
   * Stop background streaming and clean up all resources.
   */
  stop() {
    if (!this.active) return;

    logger.info('BackgroundStreamService: Stopping', { conversationId: this.conversationId });

    if (this.unregisterHandler) {
      this.unregisterHandler();
      this.unregisterHandler = null;
    }

    if (this.worker) {
      this.worker.terminate();
      this.worker = null;
    }

    if (this.connectionHeld) {
      chatStreamService.disconnect();
      this.connectionHeld = false;
    }

    this.active = false;
    this.conversationId = null;
    this.userData = null;
    this.selectedEndpoints = [];
    this.generatedTestConfigs = [];
    this.currentEndpointIndex = 0;
    this.currentPhase = null;
    this.buffer = '';
  }

  /**
   * Get current state from the worker.
   * @returns {Promise<Object|null>}
   */
  getState() {
    return new Promise((resolve) => {
      if (!this.worker || !this.active) {
        resolve(null);
        return;
      }

      const handler = (event) => {
        if (event.data.type === 'state') {
          this.worker.removeEventListener('message', handler);
          resolve({
            ...event.data.data,
            // Include service-level state for restore
            generatedTestConfigs: this.generatedTestConfigs,
            currentEndpointIndex: this.currentEndpointIndex,
            currentPhase: this.currentPhase
          });
        }
      };

      this.worker.addEventListener('message', handler);
      this.worker.postMessage({ action: 'getState' });
    });
  }

  isActive() { return this.active; }
  getConversationId() { return this.conversationId; }

  // ── Auto-progression logic ──

  /**
   * Handle stream completion and auto-trigger next step.
   * @private
   */
  _handleStreamComplete(generationType, result) {
    // Ignore stale completions from a previous phase (e.g. a delayed testconfig 'complete'
    // arriving after the phase has already transitioned to permutation and reset currentEndpointIndex).
    if (generationType !== this.currentPhase) {
      logger.warn('BackgroundStreamService: Ignoring stale stream completion', { generationType, currentPhase: this.currentPhase });
      return;
    }
    // Guard against double-firing (worker + main thread handler)
    if (this._lastCompletedPhase === `${generationType}-${this.currentEndpointIndex}`) return;
    this._lastCompletedPhase = `${generationType}-${this.currentEndpointIndex}`;

    switch (generationType) {
      case 'mapping':
        this._handleMappingComplete();
        break;
      case 'testconfig':
        this._handleTestConfigComplete(result);
        break;
      case 'permutation':
        this._handlePermutationComplete(result);
        break;
      default:
        logger.warn('BackgroundStreamService: Unknown generation type', { generationType });
        break;
    }
  }

  /**
   * Mapping complete → requires user action (endpoint selection).
   * @private
   */
  _handleMappingComplete() {
    logger.info('BackgroundStreamService: Mapping complete — action needed for endpoint selection');

    // Parse extracted endpoints from mapping buffer and save to snapshot
    try {
      const extractedEndpoints = EndpointParser.parseEndpointsIncrementally(this.buffer, null);

      // Extract Phase 1 data (globals)
      const phase1Data = EndpointParser.extractPhase1Data(this.buffer);
      if (phase1Data) {
        EndpointParser.globalPhase1Data = phase1Data;
      }

      // ── BATCH CONTINUATION (disabled — enable when backend sends <mapper_handoff>) ──
      // When enabled: detects <mapper_handoff> XML in the buffer. If continuation_required=true,
      // auto-sends a follow-up message so the backend resumes mapping. Endpoints accumulate
      // across batches via this._accumulatedEndpoints. See EndpointParser.detectMapperHandoff().
      //
      // To enable: uncomment this block.
      //
      // const handoff = EndpointParser.detectMapperHandoff(this.buffer);
      // if (handoff?.continuationRequired) {
      //   if (!this._accumulatedEndpoints) this._accumulatedEndpoints = [];
      //   for (const ep of (extractedEndpoints || [])) {
      //     const key = `${ep.method}:${ep.path}`;
      //     const idx = this._accumulatedEndpoints.findIndex(e => `${e.method}:${e.path}` === key);
      //     if (idx !== -1) this._accumulatedEndpoints[idx] = ep;
      //     else this._accumulatedEndpoints.push(ep);
      //   }
      //   this._updateProgress();
      //   this.buffer = '';
      //   this._lastCompletedPhase = null;
      //   setTimeout(() => {
      //     const extra = { files: this._initialFiles || [], handoff: {
      //       lastMappedPath: handoff.lastMappedPath, lastMappedMethod: handoff.lastMappedMethod,
      //       globalsEmitted: handoff.globalsEmitted, continuationRequired: handoff.continuationRequired
      //     }};
      //     if (handoff.promptId) extra.promptId = handoff.promptId;
      //     if (handoff.businessProcess) extra.businessProcess = handoff.businessProcess;
      //     this._sendChatMessage('', extra);
      //   }, 500);
      //   return;
      // }
      // ── END BATCH CONTINUATION ──

      if (extractedEndpoints?.length > 0) {
        const store = useGenerationStore.getState();
        const currentSession = store.activeScriptGeneration;
        if (currentSession && currentSession.conversationId === this.conversationId) {
          store.setActiveScriptGeneration({
            ...currentSession,
            workflowSnapshot: {
              ...currentSession.workflowSnapshot,
              extractedEndpoints,
              workflowStep: 'selecting'
            }
          });
        }
        logger.info('BackgroundStreamService: Extracted endpoints from mapping', { count: extractedEndpoints.length });
      }
    } catch (err) {
      logger.error('BackgroundStreamService: Failed to parse mapping endpoints', { error: err.message });
    }

    this._accumulatedEndpoints = null;
    this._setActionNeeded('Endpoints discovered! Return to select endpoints.');
  }

  /**
   * Test config complete for one endpoint → auto-trigger next or move to permutations.
   * @private
   */
  _handleTestConfigComplete(result) {
    const bufferContent = result?.raw || this.buffer || '';
    const endpoint = this.selectedEndpoints[this.currentEndpointIndex];

    if (!endpoint) {
      logger.warn('BackgroundStreamService: No endpoint at index', { index: this.currentEndpointIndex });
      return;
    }

    // Parse test config files from the buffer
    try {
      const files = EndpointParser.extractTestConfigFromResponse(bufferContent.trim());
      if (files.length > 0) {
        this.generatedTestConfigs.push({ endpoint, files });
        logger.info('BackgroundStreamService: Test config extracted', {
          endpoint: `${endpoint.method} ${endpoint.path}`,
          fileCount: files.length,
          totalConfigs: this.generatedTestConfigs.length
        });
      }
    } catch (err) {
      logger.error('BackgroundStreamService: Failed to parse test config', { error: err.message });
    }

    this.currentEndpointIndex++;
    this.buffer = '';

    // More endpoints to process?
    if (this.currentEndpointIndex < this.selectedEndpoints.length) {
      logger.info('BackgroundStreamService: Auto-triggering next test config', {
        nextIndex: this.currentEndpointIndex,
        total: this.selectedEndpoints.length
      });
      this._updateProgress();
      setTimeout(() => this._sendNextTestConfigMessage(), 500);
    } else {
      // All test configs done → mark complete and start permutations
      logger.info('BackgroundStreamService: All test configs done, starting permutations');
      this.currentEndpointIndex = 0;
      this.currentPhase = 'permutation';
      this._lastCompletedPhase = null;
      this._markTestConfigsComplete();
      this._updateProgress();
      setTimeout(() => this._sendNextPermutationMessage(), 500);
    }
  }

  /**
   * Permutation complete for one endpoint → auto-trigger next or finish.
   * @private
   */
  _handlePermutationComplete(result) {
    const endpoint = this.selectedEndpoints[this.currentEndpointIndex];
    const bufferContent = result?.raw || this.buffer || '';

    logger.info('BackgroundStreamService: Permutation complete', {
      endpoint: endpoint ? `${endpoint.method} ${endpoint.path}` : 'unknown',
      index: this.currentEndpointIndex,
      bufferLength: bufferContent.length
    });

    // Extract and store permutation CSV data for UI restore
    if (bufferContent && endpoint) {
      try {
        const extractedFiles = EndpointParser.extractPermutationFromResponse(bufferContent.trim());

        // Add linking columns (same as foreground flow in usePermutationGeneration)
        const reqFile = extractedFiles.find(f => f.filename === 'expanded_request_data.csv');
        const resFile = extractedFiles.find(f => f.filename === 'expanded_response_data.csv');
        if (reqFile?.content) {
          reqFile.content = addLinkingColumnsToRequestCSV(reqFile.content, resFile?.content || '');
        }

        if (extractedFiles.length > 0) {
          this.completedPermutations.push({
            endpoint: { method: endpoint.method, path: endpoint.path },
            files: extractedFiles
          });
          logger.info('BackgroundStreamService: Permutation data stored', {
            endpoint: `${endpoint.method} ${endpoint.path}`,
            fileCount: extractedFiles.length
          });
        }
      } catch (err) {
        logger.error('BackgroundStreamService: Failed to parse permutation data', { error: err.message });
      }
    }

    this.currentEndpointIndex++;
    this.buffer = '';

    // More endpoints?
    if (this.currentEndpointIndex < this.selectedEndpoints.length) {
      logger.info('BackgroundStreamService: Auto-triggering next permutation');
      this._updateProgress();
      setTimeout(() => this._sendNextPermutationMessage(), 500);
    } else {
      // All done — mark permutations complete, auto-create project
      logger.info('BackgroundStreamService: All generation complete');
      this._markPermutationsComplete();

      // Auto-trigger project creation in background
      if (this.projectInfo && this.userData) {
        this._updateProgress();
        this._createProjectAndUploadFiles().catch(err => {
          logger.error('BackgroundStreamService: Project creation failed', { error: err.message });
          this._setActionNeeded('Generation complete but project creation failed. Return to retry.');
        });
      } else {
        this._setActionNeeded('All test scripts generated! Return to create your project.');
      }
    }
  }

  // ── Background project creation ──

  /**
   * Create project, folders, and upload all files in the background.
   * Replicates useProjectCreationWorkflow.triggerProjectCreation() without React hooks.
   * @private
   */
  async _createProjectAndUploadFiles() {
    logger.info('BackgroundStreamService: Starting project creation', {
      projectName: this.projectInfo.projectName,
      configCount: this.generatedTestConfigs.length,
      permutationCount: this.completedPermutations.length,
      isTestSuiteMode: this.isTestSuiteMode
    });

    let projectId, projectNamePath, realProject;

    // Step 1: Create project or use existing
    if (this.isTestSuiteMode) {
      projectId = this.existingProjectId;
      projectNamePath = this.existingProjectName;
      realProject = { id: this.existingProjectId, name: this.existingProjectName };
    } else {
      const projectData = {
        name: this.projectInfo.projectName,
        description: this.projectInfo.description || '',
        version: this.projectInfo.version || '1.0.0',
        priority: this.projectInfo.priority || 'Medium',
        status: this.projectInfo.status || 'Active',
        useDefaultFolderStructure: true
      };

      realProject = await ProjectService.createProject(projectData, this.userData);
      if (!realProject) throw new Error('Failed to create project');
      projectId = realProject.id;
      projectNamePath = realProject.name;
      logger.info('BackgroundStreamService: Project created', { projectId, projectNamePath });

      // Upload permission.json — required for the project to appear in the user's project list.
      // Without this, the backend API filters it out (no user has access).
      try {
        const now = new Date().toISOString();
        const permissions = {
          owner: this.userData.userId,
          allowedUsers: [{
            userId: this.userData.userId,
            permissions: ['read', 'write', 'execute', 'delete']
          }],
          allowedGroups: [],
          private: true,
          createdAt: now,
          updatedAt: now
        };
        const permBlob = new Blob([JSON.stringify(permissions, null, 2)], { type: 'application/json' });
        const permFile = new File([permBlob], 'permission.json', { type: 'application/json' });
        await FileService.uploadFile(projectId, projectNamePath, permFile, this.userData);
        logger.info('BackgroundStreamService: permission.json uploaded');
      } catch (permErr) {
        logger.error('BackgroundStreamService: Failed to upload permission.json', { error: permErr.message });
      }

      // Upload ProjectGlobalConfig.json — project metadata
      try {
        const now = new Date().toISOString();
        const globalConfig = {
          $schema: 'tonic/project-config/v1',
          tags: [],
          createdAt: now,
          updatedAt: now
        };
        const configBlob = new Blob([JSON.stringify(globalConfig, null, 2)], { type: 'application/json' });
        const configFile = new File([configBlob], 'ProjectGlobalConfig.json', { type: 'application/json' });
        await FileService.uploadFile(projectId, projectNamePath, configFile, this.userData);
        logger.info('BackgroundStreamService: ProjectGlobalConfig.json uploaded');
      } catch (configErr) {
        logger.warn('BackgroundStreamService: Failed to upload ProjectGlobalConfig.json', { error: configErr.message });
      }
    }

    // Resolve every script's folder name once, before the configs are populated, so
    // the projectPath baked into each request_payload.json is the folder actually
    // created in Step 5. Deriving it in both places produced phantom duplicate
    // scripts (TSB-4519).
    const parseTestConfig = (testConfig) => {
      const configFile = testConfig.files.find(f => f.filename === 'request_payload.json');
      if (!configFile) return null;
      try { return JSON.parse(configFile.content); } catch (e) { return null; }
    };
    const resolvedFolderNames = resolveScriptFolderNames(
      this.generatedTestConfigs, this.customFolderNames, [], parseTestConfig
    );

    // Step 2: generatedTestConfigs already has permutation files merged (from _markPermutationsComplete)
    // Re-process configs with real project data
    const finalConfigs = this.generatedTestConfigs.map(testConfig => {
      const configFile = testConfig.files.find(f => f.filename === 'request_payload.json');
      if (!configFile) return testConfig;

      try {
        const jsonConfig = JSON.parse(configFile.content);

        // Apply custom folder name override
        const endpointKey = `${testConfig.endpoint.method}-${testConfig.endpoint.path}`;
        if (this.customFolderNames[endpointKey] && jsonConfig.tests?.[0]) {
          jsonConfig.tests[0].name = this.customFolderNames[endpointKey].toUpperCase();
        }

        const updatedConfig = JsonConfigPopulator.populateConfig(
          jsonConfig, realProject, this.userData, testConfig.endpoint, testConfig.files,
          resolvedFolderNames.get(endpointKey)
        );

        // Filter original CSVs when expanded versions exist (use .includes() for prefixed filenames)
        const hasExpandedReq = testConfig.files.some(f => f.filename.includes('expanded_request_data'));
        const hasExpandedRes = testConfig.files.some(f => f.filename.includes('expanded_response_data'));
        const updatedFiles = testConfig.files
          .filter(f => {
            // Remove originals but keep expanded versions
            if (hasExpandedReq && f.filename.includes('request_data.csv') && !f.filename.includes('expanded_')) return false;
            if (hasExpandedRes && f.filename.includes('response_data.csv') && !f.filename.includes('expanded_')) return false;
            return true;
          })
          .map(f => f.filename === 'request_payload.json'
            ? { ...f, content: JSON.stringify(updatedConfig, null, 2) }
            : f
          );

        // Add environment CSV
        const baseUrl = updatedConfig.tests?.[0]?.steps?.[0]?.endpoint?.baseUrl || '';
        updatedFiles.push({
          filename: `${testConfig.endpoint.method.toUpperCase()}_baseUrl_environments.csv`,
          content: `environment,base_url,active\nenvironment1,${baseUrl || 'https://api.example.com'},true`,
          format: 'csv',
          type: 'environment'
        });

        return { endpoint: testConfig.endpoint, files: updatedFiles };
      } catch (err) {
        logger.error('BackgroundStreamService: Config processing error', { error: err.message });
        return testConfig;
      }
    });

    // Step 4: Create folder structure
    if (!this.isTestSuiteMode) {
      await ProjectService.createFolder(projectId, '', projectNamePath, this.userData);

      const subfolders = [
        'Automation Scripts', 'Test Case Repository',
        'Test Suite Repository', 'Requirements Repository',
        'Business Processes Repository', 'Traceability Matrix Repository',
        'Risk Analysis Repository'
      ];
      for (const sub of subfolders) {
        try {
          await ProjectService.createFolder(projectId, projectNamePath, sub, this.userData);
        } catch (err) {
          logger.warn('BackgroundStreamService: Subfolder creation failed', { sub, error: err.message });
        }
      }
    }

    // Step 5: Create endpoint folders and upload files
    for (const testConfig of finalConfigs) {
      // Same name the populated configs were given above.
      const endpointKey = `${testConfig.endpoint.method}-${testConfig.endpoint.path}`;
      const folderName = resolvedFolderNames.get(endpointKey)
        || resolveScriptFolderName({
             endpoint: testConfig.endpoint,
             testConfigData: parseTestConfig(testConfig),
             customName: this.customFolderNames[endpointKey] || '',
           });

      try {
        await ProjectService.createFolder(
          projectId, `${projectNamePath}/Automation Scripts`, folderName, this.userData
        );

        for (const file of testConfig.files) {
          const mimeType = determineMimeType(file.filename);
          const blob = new Blob([file.content], { type: mimeType });
          const fileObject = new File([blob], file.filename, { type: mimeType });

          await FileService.uploadFile(
            projectId,
            `${projectNamePath}/Automation Scripts/${folderName}`,
            fileObject,
            this.userData
          );
        }

        logger.info('BackgroundStreamService: Uploaded files for endpoint', {
          endpoint: `${testConfig.endpoint.method} ${testConfig.endpoint.path}`,
          fileCount: testConfig.files.length
        });
      } catch (err) {
        logger.error('BackgroundStreamService: Failed to upload folder', {
          folderName, error: err.message
        });
      }
    }

    // Step 6: Mark complete in store
    logger.info('BackgroundStreamService: Project creation complete', { projectId });

    const store = useGenerationStore.getState();
    const currentSession = store.activeScriptGeneration;
    if (currentSession && currentSession.conversationId === this.conversationId) {
      store.setActiveScriptGeneration({
        ...currentSession,
        workflowSnapshot: {
          ...currentSession.workflowSnapshot,
          projectCreated: realProject,
          projectCreationComplete: true
        }
      });
    }

    // Dispatch projectCreated event so project views refresh their lists
    // (same event the foreground useProjects.createProject() dispatches)
    try {
      window.dispatchEvent(new CustomEvent('projectCreated', {
        detail: { project: realProject, projectId }
      }));
    } catch (e) {
      logger.warn('BSS: Failed to dispatch projectCreated event', { error: e.message });
    }

    // Invalidate the projects store cache and force a refresh so the project
    // appears in the project view when the user navigates there.
    // Small delay lets the backend index the new project before we fetch the list.
    try {
      localStorage.setItem('newProjectCreated', realProject?.name || 'BSS Project');
      localStorage.setItem('projectsUpdated', Date.now().toString());

      await new Promise(resolve => setTimeout(resolve, 2000));

      const result = await useProjectsStore.getState().refreshProjects(this.userData);
      logger.info('BSS: Projects store refreshed', {
        success: result?.success,
        projectCount: result?.data?.length,
        includesNew: result?.data?.some(p => p.id === projectId || p.name === realProject?.name)
      });
    } catch (e) {
      logger.warn('BSS: Projects store refresh failed', { error: e.message });
    }

    this._setComplete('Project created and files uploaded!');
  }

  // ── Chat message sending ──

  /**
   * Send a chat message directly via WebSocketManager.
   * @private
   */
  _sendChatMessage(messageText, extraPayload = null) {
    if (!this.userData || !this.conversationId) {
      logger.error('BackgroundStreamService: Cannot send message — missing userData or conversationId');
      return;
    }

    const payload = {
      action: 'sendMessage',
      message: messageText,
      conversationId: this.conversationId,
      userId: this.userData.userId,
      tenantId: this.userData.tenantId || '',
      s3_bucket_name: this.userData.customBuckets && this.userData.projectsBucketName
        ? this.userData.projectsBucketName
        : this.userData.fileManagementBucket || ''
    };

    // Merge extra fields (e.g., handoff, promptId, businessProcess for batch continuation)
    if (extraPayload) {
      Object.assign(payload, extraPayload);
    }

    chatStreamService.sendMessage(payload);

    logger.info('BackgroundStreamService: Chat message sent');
  }

  /**
   * Send test config generation message for the next endpoint.
   * @private
   */
  _sendNextTestConfigMessage() {
    if (!this.active) return;

    const endpoint = this.selectedEndpoints[this.currentEndpointIndex];
    if (!endpoint) return;

    logger.info('BackgroundStreamService: Sending test config message', {
      endpoint: `${endpoint.method} ${endpoint.path}`,
      index: this.currentEndpointIndex
    });

    try {
      const message = EndpointParser.createTestConfigMessage(endpoint);
      this.currentPhase = 'testconfig';
      this.buffer = '';
      this._lastCompletedPhase = null;

      // Reset worker buffer for new stream
      if (this.worker) {
        this.worker.postMessage({
          action: 'init',
          data: { activeGenerationType: 'testconfig', existingBuffer: '' }
        });
      }

      this._sendChatMessage(message);
    } catch (err) {
      logger.error('BackgroundStreamService: Failed to create test config message', { error: err.message });
      // Skip to next endpoint
      this.currentEndpointIndex++;
      if (this.currentEndpointIndex < this.selectedEndpoints.length) {
        setTimeout(() => this._sendNextTestConfigMessage(), 500);
      }
    }
  }

  /**
   * Send permutation generation message for the next endpoint.
   * @private
   */
  _sendNextPermutationMessage() {
    if (!this.active) return;

    const endpoint = this.selectedEndpoints[this.currentEndpointIndex];
    const testConfig = this.generatedTestConfigs.find(
      tc => tc.endpoint.method === endpoint?.method && tc.endpoint.path === endpoint?.path
    );

    if (!endpoint || !testConfig) {
      logger.warn('BackgroundStreamService: No endpoint or test config for permutation', {
        index: this.currentEndpointIndex,
        hasEndpoint: !!endpoint,
        hasTestConfig: !!testConfig
      });
      // Skip to next
      this.currentEndpointIndex++;
      if (this.currentEndpointIndex < this.selectedEndpoints.length) {
        setTimeout(() => this._sendNextPermutationMessage(), 500);
      } else {
        this._setActionNeeded('All test scripts generated! Return to create your project.');
      }
      return;
    }

    logger.info('BackgroundStreamService: Sending permutation message', {
      endpoint: `${endpoint.method} ${endpoint.path}`
    });

    try {
      const message = EndpointParser.createPermutationMessage(endpoint, testConfig.files);
      this.currentPhase = 'permutation';
      this.buffer = '';
      this._lastCompletedPhase = null;

      // Reset worker buffer
      if (this.worker) {
        this.worker.postMessage({
          action: 'init',
          data: { activeGenerationType: 'permutation', existingBuffer: '' }
        });
      }

      this._sendChatMessage(message);
    } catch (err) {
      logger.error('BackgroundStreamService: Failed to create permutation message', { error: err.message });
      this.currentEndpointIndex++;
      if (this.currentEndpointIndex < this.selectedEndpoints.length) {
        setTimeout(() => this._sendNextPermutationMessage(), 500);
      } else {
        this._setActionNeeded('All test scripts generated! Return to create your project.');
      }
    }
  }

  // ── Store updates ──

  /**
   * Mark test config generation as complete in the store snapshot.
   * @private
   */
  _markTestConfigsComplete() {
    const store = useGenerationStore.getState();
    const currentSession = store.activeScriptGeneration;
    if (!currentSession || currentSession.conversationId !== this.conversationId) return;

    store.setActiveScriptGeneration({
      ...currentSession,
      workflowSnapshot: {
        ...currentSession.workflowSnapshot,
        generatedTestConfigs: this.generatedTestConfigs,
        isTestConfigGenerationComplete: true,
        currentPhase: this.currentPhase
      }
    });
  }

  /**
   * Mark permutation generation as complete in the store snapshot.
   * @private
   */
  _markPermutationsComplete() {
    const store = useGenerationStore.getState();
    const currentSession = store.activeScriptGeneration;
    if (!currentSession || currentSession.conversationId !== this.conversationId) return;

    // Merge expanded permutation CSVs INTO generatedTestConfigs so Zustand snapshot
    // has the complete data regardless of which path creates the project
    const mergedConfigs = this.generatedTestConfigs.map(tc => {
      const permutation = this.completedPermutations.find(
        p => p.endpoint.method === tc.endpoint.method && p.endpoint.path === tc.endpoint.path
      );
      if (permutation?.files?.length) {
        // Remove original CSVs that are superseded by expanded versions
        const expandedNames = permutation.files.map(f => f.filename);
        const filteredFiles = tc.files.filter(f => {
          if (expandedNames.some(en => en.includes('request_data')) && f.filename.includes('request_data.csv')) return false;
          if (expandedNames.some(en => en.includes('response_data')) && f.filename.includes('response_data.csv')) return false;
          if (f.filename.includes('test_matrix_summary.md')) return false;
          return true;
        });
        return { ...tc, files: [...filteredFiles, ...permutation.files] };
      }
      return tc;
    });

    // Update instance variable so _createProjectAndUploadFiles uses merged data
    this.generatedTestConfigs = mergedConfigs;

    store.setActiveScriptGeneration({
      ...currentSession,
      workflowSnapshot: {
        ...currentSession.workflowSnapshot,
        generatedTestConfigs: mergedConfigs,
        completedPermutations: this.completedPermutations,
        isTestConfigGenerationComplete: true,
        isPermutationGenerationComplete: true,
        currentPhase: this.currentPhase
      }
    });

    logger.info('BackgroundStreamService: Permutations merged into generatedTestConfigs', {
      configCount: mergedConfigs.length,
      permutationCount: this.completedPermutations.length
    });
  }

  /**
   * Set backgroundComplete flag (all generation done, no user action required).
   * @private
   */
  _setComplete(message) {
    const store = useGenerationStore.getState();
    const currentSession = store.activeScriptGeneration;
    if (!currentSession || currentSession.conversationId !== this.conversationId) return;

    store.setActiveScriptGeneration({
      ...currentSession,
      actionNeeded: false,
      backgroundComplete: true,
      actionMessage: message,
      workflowSnapshot: {
        ...currentSession.workflowSnapshot,
        generatedTestConfigs: this.generatedTestConfigs,
        currentEndpointIndex: this.currentEndpointIndex,
        currentPhase: this.currentPhase
      }
    });

    this._sendBrowserNotification(
      currentSession.projectName || 'Script Generation',
      message
    );
  }

  /**
   * Set actionNeeded flag in the store + send notifications.
   * @private
   */
  _setActionNeeded(message) {
    const store = useGenerationStore.getState();
    const currentSession = store.activeScriptGeneration;
    if (!currentSession || currentSession.conversationId !== this.conversationId) return;

    store.setActiveScriptGeneration({
      ...currentSession,
      actionNeeded: true,
      backgroundComplete: true,
      actionMessage: message,
      // Save current workflow state so restore works
      workflowSnapshot: {
        ...currentSession.workflowSnapshot,
        generatedTestConfigs: this.generatedTestConfigs,
        currentEndpointIndex: this.currentEndpointIndex,
        currentPhase: this.currentPhase
      }
    });

    this._sendBrowserNotification(
      currentSession.projectName || 'Script Generation',
      message
    );
  }

  /**
   * Update progress in the store (no action needed, just status).
   * @private
   */
  _updateProgress() {
    const store = useGenerationStore.getState();
    const currentSession = store.activeScriptGeneration;
    if (!currentSession || currentSession.conversationId !== this.conversationId) return;

    const phaseLabel = this.currentPhase === 'testconfig' ? 'Generating configs'
      : this.currentPhase === 'permutation' ? 'Generating permutations'
      : this.currentPhase === 'mapping' ? 'Discovering endpoints'
      : 'Processing';

    store.setActiveScriptGeneration({
      ...currentSession,
      actionNeeded: false,
      backgroundComplete: false,
      actionMessage: phaseLabel,
      workflowSnapshot: {
        ...currentSession.workflowSnapshot,
        generatedTestConfigs: this.generatedTestConfigs,
        completedPermutations: this.completedPermutations || [],
        currentEndpointIndex: this.currentEndpointIndex,
        currentPhase: this.currentPhase
      }
    });
  }

  /**
   * Send a native browser notification.
   * @private
   */
  _sendBrowserNotification(title, body) {
    try {
      if (!('Notification' in window)) return;

      if (Notification.permission === 'granted') {
        new Notification(title, { body, icon: '/logo192.png' });
      } else if (Notification.permission !== 'denied') {
        Notification.requestPermission().then(permission => {
          if (permission === 'granted') {
            new Notification(title, { body, icon: '/logo192.png' });
          }
        });
      }
    } catch (err) {
      logger.debug('Browser notification not available', { error: err.message });
    }
  }
}

// Export singleton instance
export default new BackgroundStreamService();
